import { JSX, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import { TextFieldProps } from "@mui/material/TextField";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

export type DatePickerProps = {
	value: Date | null;
	label?: string;
	disabled?: boolean;
	disableFuture?: boolean;
	inputProps?: TextFieldProps;
	// Days to mark with a dot in the calendar, e.g. the days that carry an
	// evaluation.
	highlightedDates?: Date[];

	onChange: (date: Date | null) => void;
};

const DAY_KEY = "yyyy-MM-dd";

// TODO: the datepicker should handle invalid dates better.
function DatePicker(props: DatePickerProps): JSX.Element {
	const { t } = useTranslation();

	// Wrapper-only props must not reach MUI through the spread below.
	const { highlightedDates, inputProps, ...pickerProps } = props;

	const highlighted = useMemo(
		() =>
			new Set((highlightedDates ?? []).map(date => format(date, DAY_KEY))),
		[highlightedDates]
	);

	return (
		<LocalizationProvider
			dateAdapter={AdapterDateFns}
			localeText={{
				clearButtonLabel: t("clear") // TODO: a datepicker should not have a clear label but a simple x button.
			}}>
			<MuiDatePicker
				{...pickerProps}
				format={"dd/MM/yyyy"} // TODO: allow other locales as well! // TODO: set the language as well!
				slotProps={{
					actionBar: {
						actions: ["clear"]
					},
					textField: {
						...inputProps
					},
					// A dot under every marked day, typed through the owner
					// state, so no custom day component and no cast. It is
					// currentColor so it reads on a selected day as well.
					day: ownerState =>
						highlighted.has(format(ownerState.day, DAY_KEY))
							? {
									sx: {
										"&::after": {
											content: '""',
											position: "absolute",
											bottom: 2,
											left: "calc(50% - 2px)",
											width: 4,
											height: 4,
											borderRadius: "50%",
											backgroundColor: "currentColor"
										}
									}
								}
							: {}
				}}
			/>
		</LocalizationProvider>
	);
}

export default DatePicker;
